<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Dateimanager</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('file_manager')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="fetchAll"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <input ref="fileInput" hidden type="file" multiple @change="uploadFile($event)">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <aside
                    :aria-hidden="!filterSidebarOpen"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-40"
                    :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                        </div>
                        <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                        <div class="stage-filter-row mb-5">
                            <button
                                class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                type="button"
                                @click="toggleSort"
                            >
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select
                                    v-model="sort.column"
                                    class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                    @change="fetchFiles"
                                >
                                    <option value="updated_at">Letztes Update</option>
                                    <option value="name">Dateiname</option>
                                    <option value="size">Dateigröße</option>
                                    <option value="user_id">Erstellt von</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>
                        <div class="stage-filter-label">{{ $t('Search') }}</div>
                        <div class="mb-4">
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </div>
                                <input
                                    v-model.lazy="filters.search"
                                    :placeholder="$t('Search')"
                                    class="stage-filter-input form-input block pl-10 text-sm"
                                    @change="fetchFiles"
                                >
                            </div>
                        </div>
                        <div class="stage-filter-label">{{ $t('Customers') }}</div>
                        <div class="mb-3 border border-bordergray-100 rounded bg-white px-3 py-2 max-h-52 overflow-auto">
                            <label class="flex items-center py-1 cursor-pointer">
                                <input
                                    :checked="!customerScope"
                                    class="form-checkbox h-4 w-4 text-gray-600 mr-2"
                                    type="checkbox"
                                    @change="toggleCustomerFilter(0)"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ $t('All customers') }}</span>
                            </label>
                            <label
                                v-for="customer in customerFilterOptions"
                                :key="'sidebar-customer-' + customer.id"
                                class="flex items-center py-1 cursor-pointer"
                            >
                                <input
                                    :checked="isCustomerSelected(customer.id)"
                                    class="form-checkbox h-4 w-4 text-gray-600 mr-2"
                                    type="checkbox"
                                    @change="toggleCustomerFilter(customer.id)"
                                >
                                <span v-if="customer.avatarUrl" class="flex-shrink-0 inline-block mr-2">
                                    <img :src="customer.avatarUrl" alt="Avatar" class="w-5 h-5 rounded-full">
                                </span>
                                <span class="text-sm text-gray-700 truncate">{{ customer.name }}</span>
                                <span class="ml-auto text-sm text-gray-500">{{ customer.count || 0 }}</span>
                            </label>
                        </div>
                        <div v-if="categories.length > 0" class="stage-filter-label">Kategorien</div>
                        <div v-if="categories.length > 0" class="mb-3 border border-bordergray-100 rounded bg-white px-3 py-2 max-h-52 overflow-auto">
                            <label
                                v-for="category in categories"
                                :key="'sidebar-category-' + category.id"
                                class="flex items-center py-1 cursor-pointer"
                            >
                                <input
                                    :checked="isCategorySelected(category.id)"
                                    class="form-checkbox h-4 w-4 text-gray-600 mr-2"
                                    type="checkbox"
                                    @change="toggleCategoryFilter(category.id)"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                <span class="h-2.5 w-2.5 rounded-full ml-2 mr-2 flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                <span class="ml-auto text-sm text-gray-500">{{ category.files_count || 0 }}</span>
                            </label>
                        </div>
                        <div class="stage-filter-label">{{ $t('Items per page') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="file-manager-per-page"
                                v-model="perPage"
                                class="stage-filter-select"
                                :options="perPageOptions"
                                option-label="name"
                                @change="onPerPageChange"
                            />
                        </div>
                        <div class="stage-filter-label">{{ $t('Overview') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="file-manager-scope"
                                v-model="scopeFilterValue"
                                class="stage-filter-select"
                                :options="scopeOptions"
                                option-label="name"
                                @change="onScopeFilterChange"
                            />
                        </div>
                        <div v-if="hasActiveFilters" class="col-span-3">
                            <button
                                id="file-manager-filter-reset-btn"
                                type="button"
                                class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                @click="resetFilters"
                            >
                                {{ $t('Reset selection') }}
                            </button>
                        </div>
                    </div>
                </aside>

                <div class="border-b border-ultralight">
                    <div class="stage-header-surface">
                        <div class="flex items-stretch justify-between min-h-[52px]">
                            <div class="inline-flex items-center px-4 border-r border-ultralight">
                                <button
                                    :class="viewMode === 'list' ? 'file-manager-view-btn-active' : ''"
                                    class="file-manager-view-btn file-manager-header-action-btn"
                                    type="button"
                                    @click="viewMode = 'list'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"></svg-vue>
                                </button>
                                <button
                                    :class="viewMode === 'grid' ? 'file-manager-view-btn-active' : ''"
                                    class="file-manager-view-btn file-manager-header-action-btn ml-2"
                                    type="button"
                                    @click="viewMode = 'grid'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"></svg-vue>
                                </button>
                            </div>
                            <div class="ml-auto inline-flex items-stretch">
                                <app-tooltip
                                    v-if="showStorageInfoIcon"
                                    width-class="max-w-none"
                                    placement="bottom"
                                    class="inline-flex items-center mr-4"
                                >
                                        <span class="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-150 file-manager-header-action-btn" aria-label="Speicherverbrauch">
                                            <svg-vue class="w-4 h-4" icon="font-awesome.regular.server"></svg-vue>
                                        </span>
                                        <template #content>
                                            <div class="px-1 py-1 text-left">
                                                <div class="text-xs font-medium text-gray-700" style="white-space: nowrap;">
                                                    Speicherverbrauch: {{ storageUsagePercent }}%
                                                </div>
                                                <div class="mt-1 text-xs text-gray-500" style="white-space: nowrap;">
                                                    {{ storageUsedMbLabel }} / {{ storageLimitMbLabel }}
                                                </div>
                                            </div>
                                        </template>
                                </app-tooltip>
                                <div
                                    v-if="canManageCategories"
                                    v-on-clickaway="closeCategoryManager"
                                    class="relative inline-flex items-stretch border-l border-r border-ultralight"
                                >
                                    <button
                                        type="button"
                                        class="inline-flex items-center justify-center w-12 h-full text-gray-700 focus:outline-none file-manager-header-action-btn"
                                        aria-label="Categories"
                                        @click.stop="toggleCategoryManager"
                                    >
                                        <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                        <div v-show="categoryManagerOpen" class="origin-top-right z-40 absolute right-0 top-full mt-2 w-64 rounded-sm border border-bordergray-100 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)] overflow-hidden">
                                            <div class="overflow-hidden">
                                                <div class="border-b border-bordergray-100">
                                                    <category-create-input
                                                        ref="categoryManagerInput"
                                                        v-model:color="categoryDraft.color"
                                                        v-model:name="categoryDraft.name"
                                                        :maxlength="120"
                                                        :placeholder="$t('Color and category').toString()"
                                                        @submit="createCategory"
                                                    />
                                                </div>
                                                <div class="max-h-56 overflow-auto">
                                                    <div
                                                        v-for="category in categories"
                                                        :key="'manager-category-' + category.id"
                                                        class="file-manager-category-row border-b border-bordergray-100 last:border-b-0 flex items-center"
                                                    >
                                                        <div class="file-manager-category-row-content inline-flex items-center min-w-0 flex-1">
                                                            <span class="file-manager-category-row-dot rounded-full flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                            <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                                            <span class="text-xs text-gray-500 ml-2">({{ category.files_count || 0 }})</span>
                                                        </div>
                                                        <div class="file-manager-category-row-actions">
                                                            <button
                                                                v-if="canManageCategory(category)"
                                                                type="button"
                                                                class="file-manager-category-delete-btn text-gray-500 hover:text-gray-700 focus:outline-none inline-flex items-center justify-center"
                                                                aria-label="Delete category"
                                                                @click.stop="deleteCategory(category)"
                                                            >
                                                                <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                </div>
                                <div class="w-32 border-l border-ultralight">
                                    <button class="btn-stage-primary w-full h-full border-0 file-manager-header-action-btn" style="border-radius: 0;" type="button" @click="selectUploadFile">
                                        Hochladen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <template v-if="!loading && files.length < 1">
                            <empty-state />
                        </template>

                        <template v-else-if="viewMode === 'list'">
                            <div class="hidden sm:block h-full">
                                <div class="h-full overflow-auto">
                                        <table class="table-listview table-listview--zebra file-manager-list-table">
                                <thead>
                                <tr>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-title">Titel</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-type">Typ</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-size">Größe</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-ls">L/S</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-customer-ls">Kunden L/S</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-upload">Upload von</th>
                                    <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-update">Update</th>
                                    <th class="table-listview-l-th table-listview-l-th-right file-manager-list-sticky-th file-manager-col-actions">Aktionen</th>
                                </tr>
                                </thead>
                                <tbody class="bg-white">
                                <tr v-for="item in files" :key="rowKey(item)">
                                    <td class="table-listview-l-td whitespace-no-wrap file-manager-cell-nowrap file-manager-col-title">
                                        <div class="flex items-center text-sm font-medium text-gray-900 min-w-0 w-full">
                                            <svg-vue class="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" :icon="fileIcon(item.extension)"></svg-vue>
                                            <span class="inline-flex items-center min-w-0 max-w-full">
                                                <span class="file-manager-title-text">{{ item.name }}</span>
                                                <span v-if="item.category" class="file-manager-list-category-dot ml-2 flex-shrink-0" :style="{ backgroundColor: getCategoryColor(item) }"></span>
                                                <app-tooltip v-if="item.short_description" :text="shortDescriptionTooltip(item.short_description)" width-class="min-w-[8rem] max-w-[300px] break-words" class="ml-2 flex-shrink-0 inline-flex">
                                                    <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                </app-tooltip>
                                            </span>
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 uppercase file-manager-cell-nowrap file-manager-col-type">{{ item.extension || '-' }}</td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 file-manager-cell-nowrap file-manager-col-size">{{ item.size_readable }}</td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500 file-manager-cell-nowrap file-manager-col-ls">{{ item.employee_read_permissions_count }}/{{ item.employee_write_permissions_count }}</td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500 file-manager-cell-nowrap file-manager-col-customer-ls">{{ item.customer_read_permissions_count }}/{{ item.customer_write_permissions_count }}</td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 file-manager-cell-nowrap file-manager-col-upload">
                                        <div class="flex items-center min-w-0 w-full">
                                            <img
                                                :src="getUserAvatar(item.user)"
                                                :alt="item.user ? item.user.name : 'Avatar'"
                                                :title="item.user ? item.user.name : ''"
                                                class="h-6 w-6 rounded-full mr-3"
                                            >
                                            <span class="truncate block">{{ item.user ? item.user.name : '-' }}</span>
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500 file-manager-cell-nowrap file-manager-col-update">
                                        <span class="truncate block">{{ $momentFormatDateTimeAgo(item.updated_at) }}</span>
                                    </td>
                                    <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 text-right file-manager-cell-nowrap file-manager-col-actions">
                                        <div class="inline-flex items-center justify-end">
                                            <button
                                                v-if="isImageFile(item.extension)"
                                                type="button"
                                                class="inline-flex"
                                                @click.stop="openImagePreview(item)"
                                            >
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                            </button>
                                            <a :href="item.download + '?token=' + token" class="inline-flex ml-3">
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                            </a>
                                            <div v-if="canManageCategories && item.can_write && categories.length > 0" v-on-clickaway="closeItemCategoryMenu" class="relative inline-flex ml-3">
                                                <button
                                                    type="button"
                                                    class="inline-flex items-center justify-center text-gray-700"
                                                    @click.stop="toggleItemCategoryMenu(item)"
                                                >
                                                    <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                                </button>
                                                <transition
                                                    duration="100"
                                                    enter-active-class="transition ease-out duration-100"
                                                    enter-class="transform opacity-0 scale-95"
                                                    enter-to-class="transform opacity-100 scale-100"
                                                    leave-active-class="transition ease-in duration-75"
                                                    leave-class="transform opacity-100 scale-100"
                                                    leave-to-class="transform opacity-0 scale-95"
                                                >
                                                    <div v-show="isItemCategoryMenuOpen(item)" class="origin-top-right z-40 absolute right-0 mt-2 w-52 rounded shadow-lg">
                                                        <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100 max-h-56 overflow-auto" role="menu">
                                                            <button
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="assignCategory(item, null)"
                                                            >
                                                                Keine Kategorie
                                                            </button>
                                                            <button
                                                                v-for="category in categories"
                                                                :key="'assign-category-list-' + item.id + '-' + category.id"
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="assignCategory(item, category.id)"
                                                            >
                                                                <span class="inline-flex items-center">
                                                                    <span class="h-2.5 w-2.5 rounded-full mr-2" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                                    <span>{{ category.name }}</span>
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </transition>
                                            </div>
                                            <div v-if="item.can_write" v-on-clickaway="closeActionMenu" class="relative inline-flex ml-3">
                                                <button
                                                    type="button"
                                                    class="inline-flex"
                                                    @click.stop="toggleActionMenu(item)"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                                <transition
                                                    duration="100"
                                                    enter-active-class="transition ease-out duration-100"
                                                    enter-class="transform opacity-0 scale-95"
                                                    enter-to-class="transform opacity-100 scale-100"
                                                    leave-active-class="transition ease-in duration-75"
                                                    leave-class="transform opacity-100 scale-100"
                                                    leave-to-class="transform opacity-0 scale-95"
                                                >
                                                    <div v-show="isActionMenuOpen(item)" class="origin-top-right z-40 absolute right-0 mt-2 w-44 rounded shadow-lg">
                                                        <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                            <button
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="openEditFromMenu(item)"
                                                            >
                                                                Editieren
                                                            </button>
                                                            <button
                                                                v-if="item.can_delete"
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="openDeleteModal(item)"
                                                            >
                                                                Löschen
                                                            </button>
                                                        </div>
                                                    </div>
                                                </transition>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                                </table>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div class="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 file-manager-tile-grid">
                                <div
                                    v-for="item in files"
                                    :key="'grid-' + rowKey(item)"
                                    class="bg-white border border-bordergray-100 rounded file-manager-tile-item"
                                    :style="{ borderLeftColor: item.category ? getCategoryColor(item) : undefined, borderLeftWidth: item.category ? 'calc(0.28rem + 1px)' : undefined }"
                                >
                                    <div class="px-6 py-6 text-center file-manager-tile-header relative">
                                        <div v-if="canManageCategories && item.can_write && categories.length > 0" v-on-clickaway="closeItemCategoryMenu" class="absolute left-3 top-3 inline-flex">
                                            <button
                                                type="button"
                                                class="inline-flex items-center justify-center text-gray-700"
                                                @click.stop="toggleItemCategoryMenu(item)"
                                            >
                                                <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                            </button>
                                            <transition
                                                duration="100"
                                                enter-active-class="transition ease-out duration-100"
                                                enter-class="transform opacity-0 scale-95"
                                                enter-to-class="transform opacity-100 scale-100"
                                                leave-active-class="transition ease-in duration-75"
                                                leave-class="transform opacity-100 scale-100"
                                                leave-to-class="transform opacity-0 scale-95"
                                            >
                                                <div v-show="isItemCategoryMenuOpen(item)" class="origin-top-left z-40 absolute left-0 top-full mt-2 w-52 rounded shadow-lg">
                                                    <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100 max-h-56 overflow-auto" role="menu">
                                                        <button
                                                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                            type="button"
                                                            @click.stop="assignCategory(item, null)"
                                                        >
                                                            Keine Kategorie
                                                        </button>
                                                        <button
                                                            v-for="category in categories"
                                                            :key="'assign-category-grid-top-' + item.id + '-' + category.id"
                                                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                            type="button"
                                                            @click.stop="assignCategory(item, category.id)"
                                                        >
                                                            <span class="inline-flex items-center">
                                                                <span class="h-2.5 w-2.5 rounded-full mr-2" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                                <span>{{ category.name }}</span>
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </transition>
                                        </div>
                                        <svg-vue class="h-14 w-14 mx-auto mb-4" :icon="fileIcon(item.extension)"></svg-vue>
                                        <div class="flex items-center justify-center w-full text-sm text-gray-800 px-2 min-w-0">
                                            <span class="inline-flex items-center min-w-0 max-w-full">
                                                <span class="file-manager-title-text">{{ item.name }}</span>
                                                <app-tooltip
                                                    v-if="item.short_description"
                                                    :text="shortDescriptionTooltip(item.short_description)"
                                                    class="ml-2 flex-shrink-0 inline-flex"
                                                    width-class="break-words"
                                                    min-width="8rem"
                                                    max-width="200px"
                                                    :fit-content="true"
                                                    :bubble-z-index="700"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-500 flex-shrink-0" icon="font-awesome.regular.circle-info"></svg-vue>
                                                </app-tooltip>
                                            </span>
                                        </div>
                                        <div class="mt-1 text-xs text-gray-500 text-center">
                                            {{ (item.extension || '-').toUpperCase() }}, {{ item.size_readable }}
                                        </div>
                                    </div>
                                    <div class="px-4 border-t border-bordergray-100 flex items-center justify-between h-12 text-xs text-gray-500">
                                        <div class="inline-flex items-center min-w-0">
                                            <img
                                                :src="getUserAvatar(item.user)"
                                                :alt="item.user ? item.user.name : 'Avatar'"
                                                :title="item.user ? item.user.name : ''"
                                                class="h-6 w-6 rounded-full flex-shrink-0 mr-2"
                                            >
                                            <span v-if="item.updated_at" class="truncate">{{ $momentFormatDateTimeAgo(item.updated_at) }}</span>
                                        </div>
                                        <div class="inline-flex items-center">
                                            <button
                                                v-if="isImageFile(item.extension)"
                                                type="button"
                                                class="inline-flex"
                                                @click.stop="openImagePreview(item)"
                                            >
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                            </button>
                                            <a :href="item.download + '?token=' + token" class="inline-flex ml-3">
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                            </a>
                                            <div v-if="item.can_write" v-on-clickaway="closeActionMenu" class="relative inline-flex ml-3">
                                                <button
                                                    type="button"
                                                    class="inline-flex"
                                                    @click.stop="toggleActionMenu(item)"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                                <transition
                                                    duration="100"
                                                    enter-active-class="transition ease-out duration-100"
                                                    enter-class="transform opacity-0 scale-95"
                                                    enter-to-class="transform opacity-100 scale-100"
                                                    leave-active-class="transition ease-in duration-75"
                                                    leave-class="transform opacity-100 scale-100"
                                                    leave-to-class="transform opacity-0 scale-95"
                                                >
                                                    <div v-show="isActionMenuOpen(item)" class="origin-top-right z-40 absolute right-0 bottom-full mb-2 w-44 rounded shadow-lg">
                                                        <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                            <button
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="openEditFromMenu(item)"
                                                            >
                                                                Editieren
                                                            </button>
                                                            <button
                                                                v-if="item.can_delete"
                                                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                type="button"
                                                                @click.stop="openDeleteModal(item)"
                                                            >
                                                                Löschen
                                                            </button>
                                                        </div>
                                                    </div>
                                                </transition>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                </div>
                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    nav-class="relative z-30"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

        <confirm-dialog :open="deleteModal" title="Datei löschen" text="Soll die Datei wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" @close="closeDeleteModal" @confirm="deleteFile" />

        <div v-show="previewModal.open" class="app-dialog-root fixed inset-0 overflow-y-auto" style="z-index: 1200;" @click.self="closeImagePreview">
            <div class="flex items-center justify-center min-h-screen px-4 py-8">
                <div class="app-dialog-overlay fixed inset-0 transition-opacity" style="z-index: 1190;"><div class="absolute inset-0 bg-black opacity-80"></div></div>
                <div class="relative max-w-6xl w-full flex items-center justify-center" style="z-index: 1200;">
                    <div class="relative inline-block max-w-full">
                        <button
                            type="button"
                            class="absolute -top-9 right-0 z-10 inline-flex items-center justify-center w-8 h-8 p-0 bg-transparent border-0 text-white leading-none focus:outline-none"
                            @click="closeImagePreview"
                        >
                            <svg viewBox="0 0 24 24" fill="none" class="h-6 w-6">
                                <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <img
                            :src="previewModal.url"
                            :alt="previewModal.title || 'Dateivorschau'"
                            class="block max-h-[85vh] w-auto max-w-full object-contain"
                        >
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import {getAuthToken} from '@/utils/auth-token';
import {mixin as clickaway} from "vue-clickaway";
import {resolveAccessState} from "@/utils/access-state";
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryHasKey,
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";
import {canManageFileCategories} from "@/utils/sharing-access";
import CategoryCreateInput from "@/components/forms/category-create-input.vue";
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {
    buildFilePreviewUrl,
    buildShortDescriptionTooltip,
    getFileCategoryColor,
    getFileRowKey,
    isImageFileExtension,
    resolveFileIcon,
    resolveFileUserAvatar,
} from "@/utils/workspace-file-manager";

export default {
    name: "file-manager-list",
    components: {
        CategoryCreateInput,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: "Dateimanager"
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 25, [10, 25, 50, 100]);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'name');
        if (!['name', 'updated_at', 'size', 'user_id'].includes(initialSortColumn)) {
            initialSortColumn = 'name';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'asc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'asc';
        }
        let initialScope = queryString(routeQuery, queryHasKey(routeQuery, 'list_scope') ? 'list_scope' : 'scope', 'all');
        if (!['all', 'own', 'shared'].includes(initialScope)) {
            initialScope = 'all';
        }
        const initialCustomerScope = queryNumber(routeQuery, ['list_customer_id', 'customer_id'], null);

        return {
            loading: false,
            filterSidebarOpen: false,
            viewMode: 'grid',
            files: [],
            token: getAuthToken(),
            filters: {
                search: queryString(routeQuery, 'list_search', null),
            },
            sort: {
                order: initialSortOrder,
                column: initialSortColumn,
            },
            scope: initialCustomerScope !== null && initialCustomerScope > 0 ? 'all' : initialScope,
            customerScope: initialCustomerScope !== null && initialCustomerScope > 0 ? initialCustomerScope : null,
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            perPage: initialPerPage !== null ? initialPerPage : 25,
            pagination: {
                currentPage: 1,
                perPage: 25,
                total: 0,
                totalPages: 1,
            },
            counters: {
                all: 0,
                own: 0,
                shared: 0,
            },
            customerCounters: [],
            categories: [],
            selectedCategoryIds: queryNumberArray(routeQuery, 'list_categories', []),
            openActionMenu: null,
            openCategoryMenu: null,
            categoryManagerOpen: false,
            deleteModal: false,
            pendingDeleteItem: null,
            previewModal: {
                open: false,
                url: '',
                title: '',
            },
            categoryDraft: {
                name: '',
                color: UI_COLORS.text.subtle,
            },
            storage: {
                used_bytes: 0,
                used_readable: '0 B',
                has_limit: false,
                limit_bytes: null,
                limit_readable: null,
            },
        };
    },
    mounted() {
        this.fetchAll();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        isCustomerFileManagerRoute() {
            return String(this.$route.path || '').startsWith('/file-manager');
        },
        fileManagerListPath() {
            return this.isCustomerFileManagerRoute ? '/file-manager/list' : '/workspace/file-manager';
        },
        canManageCategories() {
            return canManageFileCategories(this.accessState, this.currentUser);
        },
        scopeFilterValue: {
            get() {
                return this.scope || 'all';
            },
            set(value) {
                this.scope = value || 'all';
            },
        },
        scopeOptions() {
            return [
                {id: 'all', name: `Alle Dateien (${this.counters.all || 0})`},
                {id: 'own', name: `Eigene Dateien (${this.counters.own || 0})`},
                {id: 'shared', name: `Mit mir geteilt (${this.counters.shared || 0})`},
            ];
        },
        customerFilterOptions() {
            return (this.customerCounters || []).map((customer) => ({
                id: Number(customer.id),
                name: customer.name || 'Unbekannt',
                count: Number(customer.count || 0),
                avatarUrl: this.getUserAvatar(customer),
            }));
        },
        perPageOptions() {
            const defaults = [10, 25, 50, 100];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || this.sort.order !== 'asc'
                || this.sort.column !== 'name'
                || this.scope !== 'all'
                || Number(this.customerScope || 0) > 0
                || this.selectedCategoryIds.length > 0
                || Number(this.perPage || 0) !== 25;
        },
        storageUsagePercent() {
            const limitBytes = Number(this.storage && this.storage.limit_bytes ? this.storage.limit_bytes : 0);
            if (limitBytes <= 0) {
                return 0;
            }

            const usedBytes = Number(this.storage.used_bytes || 0);
            return Math.max(0, Math.round((usedBytes / limitBytes) * 100));
        },
        showStorageInfoIcon() {
            return Number(this.storage && this.storage.limit_bytes ? this.storage.limit_bytes : 0) > 0;
        },
        storageUsedMbLabel() {
            const usedBytes = Number(this.storage && this.storage.used_bytes ? this.storage.used_bytes : 0);
            return `${Math.max(0, Math.round(usedBytes / (1024 * 1024)))} Mb`;
        },
        storageLimitMbLabel() {
            const limitBytes = Number(this.storage && this.storage.limit_bytes ? this.storage.limit_bytes : 0);
            if (limitBytes <= 0) {
                return 'Unbegrenzt';
            }

            return `${Math.max(0, Math.round(limitBytes / (1024 * 1024)))} Mb`;
        },
    },
    methods: {
        fileManagerEditPath(identifier) {
            const targetPath = this.isCustomerFileManagerRoute
                ? '/file-manager/' + identifier
                : '/workspace/file-manager/' + identifier + '/edit';

            return routeWithReturnTo(targetPath, this.$route, this.fileManagerListPath);
        },
        fetchAll() {
            this.fetchOptions();
            this.fetchFiles();
        },
        fetchOptions() {
            const self = this;
            axios.get('api/workspace/file-manager/options').then(function (response) {
                self.counters = response.data.counters || self.counters;
                self.customerCounters = response.data.customer_counters || [];
                self.categories = response.data.categories || [];
                self.storage = response.data.storage || self.storage;
            });
        },
        fetchFiles() {
            const self = this;
            self.loading = true;
            self.syncFileManagerListRouteState();
            self.openActionMenu = null;
            self.openCategoryMenu = null;
            axios.get('api/workspace/file-manager', {
                params: {
                    page: self.page,
                    perPage: self.perPage,
                    search: self.filters.search,
                    sort: self.sort,
                    scope: self.scope,
                    customer_id: self.customerScope,
                    category_ids: self.selectedCategoryIds,
                }
            }).then(function (response) {
                self.files = response.data.items || [];
                self.pagination = response.data.pagination || self.pagination;
                if (Array.isArray(response.data.categories)) {
                    self.categories = response.data.categories;
                }
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        selectUploadFile() {
            this.$refs.fileInput.click();
        },
        uploadFile(event) {
            const self = this;
            if (!event.target.files || !event.target.files.length) {
                return;
            }

            const selectedFiles = Array.from(event.target.files);
            self.loading = true;
            const uploads = selectedFiles.map((file) => {
                const formData = new FormData();
                formData.append('file', file);
                return axios.post('api/workspace/file-manager', formData, {headers: {'Content-Type': 'multipart/form-data'}});
            });

            Promise.allSettled(uploads).then(function (results) {
                const successCount = results.filter((result) => result.status === 'fulfilled').length;
                const failedCount = results.length - successCount;
                const firstFailureMessage = results
                    .filter((result) => result.status === 'rejected')
                    .map((result) => result.reason && result.reason.response && result.reason.response.data ? result.reason.response.data.message : null)
                    .find((message) => !!message);
                self.loading = false;
                self.$refs.fileInput.value = null;
                self.fetchAll();

                if (successCount > 0 && failedCount === 0) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: successCount === 1 ? '1 Datei erfolgreich hochgeladen.' : `${successCount} Dateien erfolgreich hochgeladen.`,
                        type: 'success'
                    });
                    return;
                }

                if (successCount > 0) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: firstFailureMessage
                            ? `${successCount} Dateien hochgeladen, ${failedCount} fehlgeschlagen. ${firstFailureMessage}`
                            : `${successCount} Dateien hochgeladen, ${failedCount} fehlgeschlagen.`,
                        type: 'warn'
                    });
                    return;
                }

                self.$notify({
                    title: firstFailureMessage ? self.$i18n.t('Warning').toString() : self.$i18n.t('Error').toString(),
                    text: firstFailureMessage || 'Keine Datei konnte hochgeladen werden.',
                    type: firstFailureMessage ? 'warn' : 'error'
                });
            }).catch(function () {
                self.loading = false;
                self.$refs.fileInput.value = null;
            });
        },
        setScope(scope) {
            this.scope = scope;
            this.customerScope = null;
            this.page = 1;
            this.fetchFiles();
        },
        onScopeFilterChange() {
            this.customerScope = null;
            this.page = 1;
            this.fetchFiles();
        },
        onPerPageChange() {
            this.page = 1;
            this.fetchFiles();
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        isCustomerSelected(customerId) {
            return Number(this.customerScope || 0) === Number(customerId);
        },
        toggleCustomerFilter(customerId) {
            const id = Number(customerId || 0);
            this.scope = 'all';
            this.customerScope = Number(this.customerScope || 0) === id || id < 1 ? null : id;
            this.page = 1;
            this.fetchFiles();
        },
        isCategorySelected(categoryId) {
            return this.selectedCategoryIds.includes(Number(categoryId));
        },
        toggleCategoryFilter(categoryId) {
            const id = Number(categoryId);
            if (id < 1) {
                return;
            }

            if (this.selectedCategoryIds.includes(id)) {
                this.selectedCategoryIds = this.selectedCategoryIds.filter((selectedId) => selectedId !== id);
            } else {
                this.selectedCategoryIds = [...this.selectedCategoryIds, id];
            }

            this.page = 1;
            this.fetchFiles();
        },
        toggleCategoryManager() {
            if (!this.canManageCategories) {
                return;
            }
            const shouldOpen = !this.categoryManagerOpen;
            this.categoryManagerOpen = shouldOpen;
            this.closeActionMenu();
            this.closeItemCategoryMenu();
            if (shouldOpen) {
                this.focusCategoryManagerInput();
            }
        },
        closeCategoryManager() {
            this.categoryManagerOpen = false;
        },
        focusCategoryManagerInput() {
            this.$nextTick(() => {
                const input = this.$refs.categoryManagerInput;
                if (input && typeof input.focusInput === 'function') {
                    input.focusInput();
                } else if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            });
        },
        canManageCategory(category) {
            if (!category) {
                return false;
            }

            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            if (!user) {
                return false;
            }

            return this.accessState.isAdmin || Number(category.created_by_user_id || 0) === Number(user.id || 0);
        },
        createCategory() {
            if (!this.canManageCategories) {
                return;
            }
            const self = this;
            const name = String(self.categoryDraft.name || '').trim();
            if (!name) {
                if (self.$notify) {
                    self.$notify({
                        title: self.$t('Error').toString(),
                        text: self.$t('Choose a color and a category text').toString(),
                        type: 'error',
                    });
                }
                self.$nextTick(function () {
                    const input = self.$refs.categoryManagerInput;
                    if (input && typeof input.focusInput === 'function') {
                        input.focusInput();
                    } else if (input && typeof input.focus === 'function') {
                        input.focus();
                    }
                });
                return;
            }

            axios.post('api/workspace/file-manager/categories', {
                name: name,
                color: self.categoryDraft.color || UI_COLORS.text.subtle,
            }).then(function (response) {
                self.categories = response.data.categories || self.categories;
                self.categoryDraft.name = '';
                self.categoryDraft.color = UI_COLORS.text.subtle;
                self.fetchFiles();
            });
        },
        deleteCategory(category) {
            if (!this.canManageCategories) {
                return;
            }
            const self = this;
            if (!category || !category.id) {
                return;
            }

            axios.delete(`api/workspace/file-manager/categories/${category.id}`)
                .then(function (response) {
                const deletedCategoryId = Number(category.id);
                self.categories = response.data.categories || [];
                self.selectedCategoryIds = self.selectedCategoryIds.filter((id) => id !== deletedCategoryId);
                self.fetchFiles();
            }).catch(function () {
            });
        },
        resetFilters() {
            this.filters.search = null;
            this.sort.order = 'asc';
            this.sort.column = 'name';
            this.scope = 'all';
            this.customerScope = null;
            this.selectedCategoryIds = [];
            this.perPage = 25;
            this.page = 1;
            this.fetchAll();
        },
        syncFileManagerListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_scope',
                'list_customer_id',
                'list_categories',
                'scope',
                'customer_id',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 25), 25);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'name');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'asc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            setQueryValue(nextQuery, 'list_scope', this.scope, 'all');
            setQueryValue(nextQuery, 'list_customer_id', Number(this.customerScope || 0) > 0 ? Number(this.customerScope) : null);
            setQueryValue(nextQuery, 'list_categories', this.selectedCategoryIds, []);

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        isScopeActive(scope) {
            return this.scope === scope && !this.customerScope;
        },
        toggleSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.fetchFiles();
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.fetchFiles();
            }
        },
        openEdit(item) {
            if (!item || !item.can_write) {
                return;
            }

            const identifier = item.id || item.uuid;
            if (!identifier) {
                return;
            }

            this.$router.push(this.fileManagerEditPath(identifier));
        },
        openEditFromMenu(item) {
            this.closeActionMenu();
            this.openEdit(item);
        },
        actionMenuKey(item) {
            return `action-${this.rowKey(item)}`;
        },
        categoryMenuKey(item) {
            return `category-${this.rowKey(item)}`;
        },
        isActionMenuOpen(item) {
            return this.openActionMenu === this.actionMenuKey(item);
        },
        isItemCategoryMenuOpen(item) {
            return this.openCategoryMenu === this.categoryMenuKey(item);
        },
        toggleActionMenu(item) {
            if (!item || !item.can_write) {
                return;
            }
            const key = this.actionMenuKey(item);
            this.openCategoryMenu = null;
            this.categoryManagerOpen = false;
            this.openActionMenu = this.openActionMenu === key ? null : key;
        },
        toggleItemCategoryMenu(item) {
            if (!this.canManageCategories) {
                return;
            }
            const key = this.categoryMenuKey(item);
            this.openActionMenu = null;
            this.categoryManagerOpen = false;
            this.openCategoryMenu = this.openCategoryMenu === key ? null : key;
        },
        closeActionMenu() {
            this.openActionMenu = null;
        },
        closeItemCategoryMenu() {
            this.openCategoryMenu = null;
        },
        assignCategory(item, categoryId) {
            if (!this.canManageCategories || !item || !item.can_write) {
                return;
            }

            const self = this;
            const identifier = item.uuid || item.id;
            axios.patch(`api/workspace/file-manager/${identifier}/category`, {
                category_id: categoryId || null,
            }).then(function () {
                const selectedCategory = categoryId
                    ? self.categories.find((category) => Number(category.id) === Number(categoryId))
                    : null;
                item.category_id = categoryId ? Number(categoryId) : null;
                item.category = selectedCategory ? {
                    id: Number(selectedCategory.id),
                    name: selectedCategory.name,
                    color: selectedCategory.color,
                } : null;
                self.closeItemCategoryMenu();
                self.fetchOptions();
            });
        },
        openDeleteModal(item) {
            if (!item || !item.can_delete) {
                return;
            }

            this.pendingDeleteItem = item;
            this.closeActionMenu();
            this.closeItemCategoryMenu();
            this.closeCategoryManager();
            this.deleteModal = true;
        },
        openImagePreview(item) {
            if (!item || !this.isImageFile(item.extension)) {
                return;
            }

            this.previewModal = {
                open: true,
                url: this.buildPreviewUrl(item),
                title: item.name || 'Dateivorschau',
            };
        },
        closeImagePreview() {
            this.previewModal = {
                open: false,
                url: '',
                title: '',
            };
        },
        closeDeleteModal() {
            this.deleteModal = false;
            this.pendingDeleteItem = null;
        },
        deleteFile() {
            const item = this.pendingDeleteItem;
            if (!item || !item.can_delete) {
                this.closeDeleteModal();
                return;
            }

            const identifier = item.uuid || item.id;
            if (!identifier) {
                this.closeDeleteModal();
                return;
            }

            const self = this;
            self.loading = true;
            axios.delete('api/workspace/file-manager/' + identifier).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('The entry has been deleted').toString(),
                    type: 'success'
                });
                self.closeDeleteModal();
                self.fetchAll();
            }).catch(function () {
                self.loading = false;
                self.closeDeleteModal();
            });
        },
        getCategoryColor: getFileCategoryColor,
        rowKey: getFileRowKey,
        fileIcon: resolveFileIcon,
        getUserAvatar: resolveFileUserAvatar,
        buildPreviewUrl(item) {
            return buildFilePreviewUrl(item, this.token);
        },
        isImageFile: isImageFileExtension,
        shortDescriptionTooltip: buildShortDescriptionTooltip,
    }
}
</script>

<style scoped>
.file-manager-tile-item {
    position: relative;
    z-index: 1;
    overflow: visible;
}

.file-manager-list-category-dot {
    display: inline-flex;
    width: 0.625rem;
    min-width: 0.625rem;
    max-width: 0.625rem;
    height: 0.625rem;
    min-height: 0.625rem;
    max-height: 0.625rem;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    vertical-align: middle;
}

.file-manager-category-tag-icon {
    color: currentColor;
}

.file-manager-category-row {
    padding: 0.5rem 0;
}

.file-manager-category-row-content {
    padding-left: 0.7rem;
}

.file-manager-category-row-dot {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
}

.file-manager-category-row-actions {
    width: 2.35rem;
    min-width: 2.35rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.file-manager-category-delete-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease-in-out;
}

.file-manager-category-row:hover .file-manager-category-delete-btn {
    opacity: 1;
    pointer-events: auto;
}

.file-manager-tile-header {
    height: 11.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.file-manager-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 1.75rem;
    width: 1.75rem;
    color: var(--ui-text-default);
    border: 0;
    background: transparent;
    border-radius: 0.25rem;
}

.file-manager-view-btn-active {
    color: var(--ui-text-subtle);
    cursor: default;
}

.file-manager-header-action-btn {
    cursor: pointer;
}

.w-35 {
    width: 8.75rem;
}

.file-manager-view-btn:focus,
.file-manager-view-btn:focus-visible,
.file-manager-view-btn:active {
    outline: none !important;
    box-shadow: none !important;
}

.file-manager-count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.6rem;
    height: 1.15rem;
    padding: 0 0.32rem;
    border: 1px solid var(--ui-border-strong);
    border-radius: 9999px;
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1;
    color: var(--ui-text-default);
    background-color: var(--ui-surface-raised);
}

.file-manager-sort-item {
    border: 1px solid transparent;
    border-radius: 0;
}

.file-manager-sort-item-active {
    background-color: var(--ui-border-subtle);
}

.file-manager-sort-item:focus,
.file-manager-sort-item:focus-visible,
.file-manager-sort-item:active {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
}

.file-manager-shell button:focus,
.file-manager-shell button:focus-visible,
.file-manager-shell button:active {
    outline: none !important;
    box-shadow: none !important;
}

.file-manager-tile-item:hover,
.file-manager-tile-item:focus-within {
    z-index: 600;
}

.file-manager-list-sticky-th {
    position: sticky;
    top: 0;
    z-index: 10;
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
    background-color: var(--ui-surface-raised);
    white-space: nowrap;
}

.file-manager-list-table {
    min-width: 100%;
    border-bottom: 1px solid var(--border-ultralight);
    table-layout: auto;
}

.file-manager-cell-nowrap {
    white-space: nowrap;
}

.file-manager-title-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.file-manager-col-title {
    width: 20%;
    min-width: 10%;
    max-width: 30%;
}

.file-manager-col-type {
    width: 4.2rem;
}

.file-manager-col-size {
    width: 5.8rem;
}

.file-manager-col-ls {
    width: 3.8rem;
}

.file-manager-col-customer-ls {
    width: 6.4rem;
}

.file-manager-col-upload {
    width: 10.8rem;
}

.file-manager-col-update {
    width: 7.2rem;
}

.file-manager-col-actions {
    width: 6.2rem;
}

@media (min-width: 1400px) {
.file-manager-tile-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}
}

@media (min-width: 1800px) {
.file-manager-tile-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
}
}
</style>
